import { Button, ChipButton } from "@components/controls/Button"
import { Checkbox } from "@components/controls/Checkbox"
import { SimpleSelect } from "@components/controls/SimpleSelect"
import { DynamicTextArea, TextInput } from "@components/controls/TextInput"
import { FormLayout as F } from "@components/forms/lib/ZodForm"
import { LoadingResults } from "@components/layout/LoadingResults"
import { Scroller } from "@components/layout/Scroller"
import { IconAdd, IconSubtract } from "@components/svg/Icons"
import { useBranches, useExpertise, useUpdateTeamName } from "@hooks/queries"
import { HamburgerMenuIcon } from "@radix-ui/react-icons"
import { motion, Reorder, useDragControls } from "framer-motion"
import { ComponentProps, ComponentType, useEffect, useMemo, useRef, useState } from "react"
import { z } from "zod"
import { PortfolioEdit, saveProfileImages } from "../components/PortfolioEdit"
import { UserOrBusinessProfile } from "../profile-type"
import { BusinessProfileFragment } from "../../../types.graphql"
import { displayErrorAlert } from "@hooks/useAlertOverlay"

type EditorProps = {
  data: UserOrBusinessProfile
  onChange: (data: UserOrBusinessProfile) => void
}

const LINK_TYPES = {
  website: "Website",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  x: "X (Twitter)",
}

export type ProfileEditFormType<TErrors> = {
  label: string
  validate: (data: UserOrBusinessProfile) => { valid: boolean; errors: TErrors }
  editor: ComponentType<EditorProps & { errors?: TErrors }>
  onSave?: (data: UserOrBusinessProfile) => Promise<UserOrBusinessProfile>
}

function editor<TErrors>(def: ProfileEditFormType<TErrors>): ProfileEditFormType<TErrors> {
  return def
}

export const PROFILE_EDIT_FORMS = {
  contact: editor({
    label: "Public Contact Details",
    validate: (data) => {
      const errors = {
        email: "",
        phone: "",
        links: {} as Record<string, string>,
      }
      if (!!data.publicProfileData?.publicEmail && z.string().email().safeParse(data.publicProfileData?.publicEmail!).success === false) {
        errors.email = "Please enter a valid email address, or leave blank"
      }
      data.publicProfileData?.links?.forEach((link) => {
        if (link?.url && z.string().url().optional().safeParse(link?.url).success === false) {
          errors.links[link?.type ?? ""] = "Please enter a valid URL, or leave blank"
        }
      })
      const hasErrors = !!errors.email || !!errors.phone || Object.values(errors.links).some((e) => !!e)
      return {
        valid: !hasErrors,
        errors,
      }
    },
    editor: ({ data, onChange, errors }) => {
      return (
        <div className="w-full md:w-[600px]">
          <F.Grid>
            <F.Field label="Public Email" error={errors?.email}>
              <TextInput
                type="email"
                placeholder="you@internet.com"
                value={data.publicProfileData?.publicEmail! ?? ""}
                onChange={(e) =>
                  onChange({
                    ...data,
                    publicProfileData: { ...data.publicProfileData!, publicEmail: e.target.value! },
                  })
                }
              />
            </F.Field>
            <F.Field label="Public Phone" error={errors?.phone}>
              <TextInput
                type="tel"
                placeholder="0400 000 000"
                value={data.publicProfileData?.publicPhone! ?? ""}
                onChange={(e) =>
                  onChange({
                    ...data,
                    publicProfileData: { ...data.publicProfileData!, publicPhone: e.target.value! },
                  })
                }
              />
            </F.Field>
            {Object.entries(LINK_TYPES).map(([key, label]) => (
              <F.Field label={label} key={key} error={errors?.links[key]}>
                <TextInput
                  type={key === "website" ? "url" : "text"}
                  inputMode="url"
                  placeholder=""
                  value={data.publicProfileData?.links?.find((l) => l?.type === key)?.url! ?? ""}
                  onChange={(e) => {
                    const links = data.publicProfileData?.links?.filter((l) => l?.type !== key) ?? []
                    links.push({ type: key, url: e.target.value! })
                    onChange({
                      ...data,
                      publicProfileData: { ...data.publicProfileData!, links },
                    })
                  }}
                />
              </F.Field>
            ))}
          </F.Grid>
        </div>
      )
    },
  }),
  expertise: editor({
    label: "Expertise",
    validate: (data) => {
      return {
        valid: true,
        errors: {},
      }
    },
    editor: ({ data, onChange }) => {
      const expertise = useExpertise(undefined)
      const [group, setGroup] = useState<string | null>(null)

      const groups = useMemo(() => {
        const groups = new Map<string, { id: number; label: string; slug: string; name: string }[]>()
        expertise.data?.expertiseItems?.nodes.forEach((item) => {
          const parts = item.name?.split(/\s+[–——]\s+/, 2)
          let group = ""
          let label = ""
          if (parts?.length === 2) {
            group = parts[0]
            label = parts.slice(1).join(" - ")
          } else {
            group = "Other"
            label = item.name!
          }
          if (!groups.has(group)) {
            groups.set(group, [])
          }
          groups.get(group)?.push({ id: item.databaseId!, label, name: item.name!, slug: item.slug! })
        })
        return [...groups.entries()].sort(([g1], [g2]) => (g1 < g2 ? -1 : 1))
      }, [expertise.data])

      useEffect(() => {
        if (group === null && groups.length > 0) {
          setGroup(groups[0][0])
        }
      }, [groups])

      return (
        <Scroller className="h-[60vh] w-full md:w-[500px]">
          {expertise.isLoading ? <LoadingResults /> : null}
          <div className="flex flex-col gap-x-3 gap-y-3">
            {groups.map(([group, items]) => (
              <>
                <h4 className="type-title-xs-medium">{group}</h4>
                <div className="flex flex-wrap gap-y-2 gap-x-3">
                  {items.map((item) => (
                    <label key={item.id} className="flex gap-2 items-center">
                      <Checkbox
                        size="small"
                        checked={data.publicProfileData?.expertise?.some((e) => e?.databaseId === item.id)}
                        onChange={(checked) => {
                          if (!checked) {
                            onChange({
                              ...data,
                              publicProfileData: {
                                ...data.publicProfileData!,
                                expertise: data.publicProfileData?.expertise?.filter((e) => e?.databaseId !== item.id) ?? [],
                              },
                            })
                          } else {
                            onChange({
                              ...data,
                              publicProfileData: {
                                ...data.publicProfileData!,
                                expertise: [...(data.publicProfileData?.expertise ?? []), { databaseId: item.id, name: item.name, slug: item.slug }],
                              },
                            })
                          }
                        }}
                      />
                      <div>{item.label}</div>
                    </label>
                  ))}
                </div>
              </>
            ))}
          </div>
        </Scroller>
      )
    },
  }),
  experience: editor({
    label: "Experience",
    validate: (data) => {
      return {
        valid: true,
        errors: {},
      }
    },
    editor: ({ data, onChange }) => {
      const items = (data.publicProfileData?.experience ?? []) as { from: string; to: string; name: string; role: string; id: string }[]
      for (let item of items) {
        if (!item.id) item.id = Math.random().toString()
      }

      const modernYears = useMemo(() => {
        const years = ["Present"]
        for (let i = new Date().getFullYear(); i > 1950; i--) {
          years.push(i.toString())
        }
        return years.map((y) => ({ value: y, label: y }))
      }, [])

      const outerRef = useRef<HTMLDivElement>(null!)

      return (
        <Scroller className="max-h-[60vh]">
          <motion.div>
            <Reorder.Group
              values={items}
              onReorder={(items) => {
                onChange({
                  ...data,
                  publicProfileData: {
                    ...data.publicProfileData!,
                    experience: items,
                  },
                })
              }}
              className="flex flex-col gap-4"
              ref={outerRef}
            >
              {items.map((item, i) => {
                const update = (value: Partial<typeof item>) => {
                  onChange({
                    ...data,
                    publicProfileData: {
                      ...data.publicProfileData!,
                      experience: items.map((e) => (e.id === item.id ? { ...e, ...value } : e)),
                    },
                  })
                }
                return (
                  <ReorderItem className="flex gap-4 items-center" value={item} key={item.id} dragConstraints={outerRef} enabled={items.length > 1}>
                    <div className="grid grid-cols-2 md:grid-flow-col-dense gap-3 items-center flex-1">
                      <F.Field label="Where" grow>
                        <TextInput value={item.name} onChange={(e) => update({ name: e.target.value })} placeholder="Where" maxLength={40} />
                      </F.Field>
                      <F.Field label="Role" grow>
                        <TextInput value={item.role} onChange={(e) => update({ role: e.target.value })} placeholder="Role" maxLength={50} />
                      </F.Field>
                      <F.Field label="From" grow className="md:w-[90px]">
                        <SimpleSelect
                          value={item.from}
                          onChange={(e) => {
                            update({ from: e.currentTarget.value, to: e.currentTarget.value === "" ? "" : item.to })
                          }}
                          options={[{ label: "", value: "" }, ...modernYears.slice(1)]}
                        />
                      </F.Field>
                      <F.Field label="To" grow className="md:w-[90px]">
                        <SimpleSelect
                          value={item.to}
                          onChange={(e) => update({ to: e.currentTarget.value })}
                          options={[{ label: "", value: "" }, ...modernYears]}
                          disabled={!item.from}
                        />
                      </F.Field>
                    </div>
                    <div className="self-center md:self-end">
                      <Button
                        type="button"
                        rightIcon={<IconSubtract />}
                        size="square"
                        variant="light"
                        onClick={() => {
                          onChange({
                            ...data,
                            publicProfileData: {
                              ...data.publicProfileData!,
                              experience: items.filter((e) => e.id !== item.id),
                            },
                          })
                        }}
                      />
                    </div>
                  </ReorderItem>
                )
              })}
            </Reorder.Group>
            <div className="flex justify-end mt-3">
              <ChipButton
                variant="outline"
                leftIcon={<IconAdd />}
                type="button"
                disabled={items.length > 10}
                onClick={() => {
                  onChange({
                    ...data,
                    publicProfileData: {
                      ...data.publicProfileData!,
                      experience: [
                        ...items,
                        {
                          from: "",
                          to: "",
                          name: "",
                          role: "",
                        },
                      ],
                    },
                  })
                }}
              >
                Add Experience
              </ChipButton>
            </div>
          </motion.div>
        </Scroller>
      )
    },
  }),
  title: editor({
    label: "Professional Title",
    validate: (data) => {
      return {
        valid: true,
        errors: {},
      }
    },
    editor: ({ data, onChange }) => {
      return (
        <div className="w-[400px] max-w-[80vw]">
          <F.Stack>
            <F.Field label={"Role, Profession or Job Title"} grow>
              <TextInput
                value={data.publicProfileData?.title! ?? ""}
                placeholder={"eg. Graphic Designer"}
                maxLength={30}
                onChange={(e) =>
                  onChange({
                    ...data,
                    publicProfileData: { ...data.publicProfileData!, title: e.target.value! },
                  })
                }
              />
            </F.Field>
          </F.Stack>
        </div>
      )
    },
  }),
  businessTypeAndLocation: editor({
    label: "Business Type and Location",
    validate: (data) => {
      return {
        valid: true,
        errors: {},
      }
    },
    editor: ({ data, onChange }) => {
      if (data.__typename !== "Business") {
        return null
      }
      const branches = useBranches(undefined)
      return (
        <div className="w-[400px] max-w-[80vw]">
          <F.Stack>
            <F.Field label="Business Type" grow>
              <TextInput
                value={data.publicProfileData?.title ?? ""}
                placeholder="eg. Digital Agency"
                required
                maxLength={50}
                onChange={(e) =>
                  onChange({
                    ...data,
                    publicProfileData: {
                      ...data.publicProfileData!,
                      title: e.target.value!,
                    },
                  })
                }
              />
            </F.Field>
            <F.Field label="Location" grow>
              <div className="flex flex-wrap gap-2">
                {branches.data?.branches?.nodes
                  .filter((branch) => branch.branchSettings?.branchFlags?.includes("canJoin"))
                  .map((branch) => (
                    <label key={branch.databaseId} className="flex gap-2 items-center">
                      <Checkbox
                        size="small"
                        checked={data.businessData?.branch?.some((b) => b?.slug === branch.slug)}
                        onChange={(checked) => {
                          if (!checked) {
                            onChange({
                              ...data,
                              businessData: {
                                ...data.businessData!,
                                branch: data.businessData?.branch?.filter((b) => b?.slug !== branch.slug) ?? [],
                              },
                            })
                          } else {
                            onChange({
                              ...data,
                              businessData: {
                                ...data.businessData!,
                                branch: [...(data.businessData?.branch ?? []), { slug: branch.slug, shortName: branch.name }],
                              },
                            })
                          }
                        }}
                      />
                      {branch.name}
                    </label>
                  ))}
              </div>
            </F.Field>
          </F.Stack>
        </div>
      )
    },
  }),
  businessName: editor({
    label: "Business Name",
    validate: (data) => {
      if (data.__typename == "Business" && data.title?.length! < 1) {
        return {
          valid: false,
          errors: {},
        }
      }
      return {
        valid: true,
        errors: {},
      }
    },
    onSave: async (data) => {
      if (data.__typename !== "Business") {
        return data
      }
      return await useUpdateTeamName
        .mutate({
          newName: data.title!,
        })
        .then((result) => result.updateBusinessTeamName?.business!)
    },
    editor: ({ data, onChange }) => {
      if (data.__typename !== "Business") {
        return null
      }
      return (
        <div className="w-[400px] max-w-[80vw]">
          <F.Stack>
            <F.Field label="Business Name" grow>
              <TextInput
                value={data.title ?? ""}
                placeholder="Your business name"
                required
                maxLength={50}
                onChange={(e) =>
                  onChange({
                    ...data,
                    title: e.target.value!,
                  })
                }
              />
            </F.Field>
          </F.Stack>
        </div>
      )
    },
  }),
  introduction: editor({
    label: "Introduction",
    validate: (data) => {
      return {
        valid: true,
        errors: {},
      }
    },
    editor: ({ data, onChange }) => {
      return (
        <div className="w-full md:w-[600px]">
          <F.Stack>
            <F.Field label="Introduction" note="Max 800 characters" grow>
              <DynamicTextArea
                value={data.publicProfileData?.introduction! ?? ""}
                placeholder="Tell us a bit about yourself"
                maxLength={800}
                className="min-h-[100px] max-h-[60vh]"
                onChange={(e) =>
                  onChange({
                    ...data,
                    publicProfileData: { ...data.publicProfileData!, introduction: e.target.value! },
                  })
                }
              />
            </F.Field>
          </F.Stack>
        </div>
      )
    },
  }),
  clients: editor({
    label: "Selected Clients",
    validate: (data) => {
      return {
        valid: true,
        errors: {},
      }
    },
    editor: ({ data, onChange }) => {
      const items = (data.publicProfileData?.featuredClients?.clients ?? []) as { name: string; description: string; id: string }[]
      for (let item of items) {
        if (!item.id) item.id = Math.random().toString()
      }

      const outerRef = useRef<HTMLDivElement>(null!)

      return (
        <div className="w-full md:w-[600px]">
          <Scroller className="h-[60vh] md:w-[500px]">
            <F.Stack>
              <F.Field label="Introduction" grow note="You can use this space to talk more broadly about the kinds of client work you do.">
                <DynamicTextArea
                  value={data.publicProfileData?.featuredClients?.introduction! ?? ""}
                  placeholder=""
                  maxLength={200}
                  onChange={(e) =>
                    onChange({
                      ...data,
                      publicProfileData: {
                        ...data.publicProfileData!,
                        featuredClients: {
                          clients: data.publicProfileData?.featuredClients?.clients ?? [],
                          introduction: e.target.value!,
                        },
                      },
                    })
                  }
                />
              </F.Field>
              <F.Field label="Selected Clients">
                <motion.div className="pt-4">
                  <Reorder.Group
                    values={items}
                    onReorder={(items) => {
                      onChange({
                        ...data,
                        publicProfileData: {
                          ...data.publicProfileData!,
                          featuredClients: {
                            ...data.publicProfileData?.featuredClients!,
                            clients: items,
                          },
                        },
                      })
                    }}
                    className="flex flex-col gap-4"
                    ref={outerRef}
                  >
                    {items.map((item, i) => {
                      const update = (value: Partial<typeof item>) => {
                        onChange({
                          ...data,
                          publicProfileData: {
                            ...data.publicProfileData!,
                            featuredClients: {
                              ...data.publicProfileData?.featuredClients!,
                              clients: items.map((e) => (e.id === item.id ? { ...e, ...value } : e)),
                            },
                          },
                        })
                      }
                      return (
                        <ReorderItem className="flex gap-4" value={item} key={item.id} dragConstraints={outerRef} enabled={items.length > 1}>
                          <div className="flex flex-col w-full items-stretch md:flex-row gap-3 md:items-start flex-1">
                            <F.Field label="Client Name" grow required className="flex-auto md:grow-[0.5]">
                              <TextInput value={item.name} onChange={(e) => update({ name: e.target.value })} required maxLength={40} />
                            </F.Field>
                            <F.Field label="Description" grow className="flex-auto md:grow-[1]">
                              <DynamicTextArea value={item.description} onChange={(e) => update({ description: e.target.value })} maxLength={100} />
                            </F.Field>
                          </div>
                          <F.Field label={<>&nbsp;</>} className="flex-none">
                            <Button
                              type="button"
                              rightIcon={<IconSubtract />}
                              size="square"
                              variant="light"
                              onClick={() => {
                                onChange({
                                  ...data,
                                  publicProfileData: {
                                    ...data.publicProfileData!,
                                    featuredClients: {
                                      ...data.publicProfileData?.featuredClients!,
                                      clients: items.filter((e) => e.id !== item.id),
                                    },
                                  },
                                })
                              }}
                            />
                          </F.Field>
                        </ReorderItem>
                      )
                    })}
                  </Reorder.Group>
                  <div className="flex justify-end mt-3">
                    <ChipButton
                      variant="outline"
                      leftIcon={<IconAdd />}
                      type="button"
                      disabled={items.length > 10}
                      onClick={() => {
                        onChange({
                          ...data,
                          publicProfileData: {
                            ...data.publicProfileData!,
                            featuredClients: {
                              ...data.publicProfileData?.featuredClients!,
                              clients: [
                                ...items,
                                {
                                  name: "",
                                  description: "",
                                },
                              ],
                            },
                          },
                        })
                      }}
                    >
                      Add Client
                    </ChipButton>
                  </div>
                </motion.div>
              </F.Field>
            </F.Stack>
          </Scroller>
        </div>
      )
    },
  }),
  gallery: editor({
    label: "Portfolio Gallery",
    editor: ({ data, onChange }) => {
      return (
        <div>
          <PortfolioEdit
            awards={data.awards as any}
            items={(data.selectedGalleryItems as any) ?? []}
            onChange={(items) => {
              onChange({
                ...data,
                selectedGalleryItems: items,
              })
            }}
          />
        </div>
      )
    },
    onSave: (data) => {
      return saveProfileImages(data.selectedGalleryItems as any, data.__typename === "Business")!
    },
    validate: (data) => ({ valid: true, errors: {} }),
  }),
} satisfies Record<string, ProfileEditFormType<any>>

function ReorderItem({ enabled, ...props }: ComponentProps<typeof Reorder.Item> & { enabled?: boolean }) {
  const controls = useDragControls()

  return (
    <Reorder.Item {...props} dragListener={false} dragControls={controls}>
      {enabled !== false && (
        <F.Field label={<>&nbsp;</>} className="flex-none">
          <button type="button" className="cursor-grab active:cursor-grabbing h-button" onPointerDown={(e) => controls.start(e)}>
            <HamburgerMenuIcon />
          </button>
        </F.Field>
      )}
      {props.children}
    </Reorder.Item>
  )
}
